import {type ReactNode, useCallback, useMemo, useRef, useState} from 'react'

import {BoardDragProvider} from '../drag/BoardDragProvider'
import {BoardFocusProvider} from '../focus/BoardFocusProvider'
import {BoardReportingProvider} from '../reporting/BoardReportingProvider'
import type {BoardReporting} from '../reporting/types'
import {BOARD_AUTO_SCROLL_RATE, CELL_AUTO_SCROLL_RATE} from '../scroll/settings'
import {BoardSelectionProvider} from '../selection/BoardSelectionProvider'
import type {BoardSelectedCard} from '../selection/types'
import type {
  BoardCell,
  BoardData,
  BoardItemDragState,
  BoardItemsMove,
  BoardLabels,
  CellAddress,
  ItemId,
  RowKey,
} from '../types'
import {createCellItemIndex} from './cell-item-index'
import {composeCellKey} from './cell-key'
import {
  type BoardCardDragPolicy,
  BoardCardDragPolicyContext,
  BoardCellOrderingContext,
  BoardCollapsedRowsContext,
  type BoardConfig,
  BoardConfigContext,
  BoardDataContext,
  type BoardDataContextValue,
  BoardElementRefContext,
  type BoardHeadingElementLevel,
  type BoardHeadingLevel,
  type BoardHeadingLevels,
  BoardHeadingLevelsContext,
  type BoardItemDropPolicy,
  BoardItemDropPolicyContext,
  type BoardPaging,
  BoardPagingContext,
  BoardRowCollapseContext,
  DEFAULT_BOARD_HEADING_LEVEL,
} from './contexts'

export type BoardProviderProps<TItem> = BoardPaging &
  BoardReporting & {
    data: BoardData<TItem>
    /** The caller's stable identity for a card. */
    getItemId: (item: TItem) => ItemId
    /** The card's human-readable name, used for accessible names and announcements. */
    getItemLabel: (item: TItem) => string
    /**
     * Whether one card may be dragged, and why not when it may not. Defaults to draggable
     * for every card on a board that moves cards at all.
     *
     * The board holds no rule about who may move what: it reads no permission, asks
     * nothing about what kind of thing a card is, and applies nothing of its own on top of
     * the answer. Whatever this returns is the whole of which cards may move.
     *
     * Whether the board moves any is the separate question
     * {@link BoardProviderProps.onItemsMove} answers, and it is answered first: leave that
     * out and no card is a drag source, whatever this says.
     *
     * A refusal carries the caller's own reason, which reaches a card through
     * `useBoardCard`'s `dragRefusal`. Which cells a card can land in is the separate
     * question {@link BoardProviderProps.canDropItem} answers, per drop.
     *
     * Called with the caller's own item, resolved from the card's address against the
     * `data` above, so a card the caller's data no longer holds is left draggable rather
     * than refused on its behalf.
     */
    getItemDragState?: (item: TItem) => BoardItemDragState
    /** Every user-visible string. The package ships none of its own. */
    labels: BoardLabels
    /**
     * Whether the caller renders a name for each row. A board with no swimlane has one
     * row that means nothing to the reader, so its cells are named after their column
     * alone. Defaults to `false`.
     *
     * The caller states this rather than the board deriving it from `rows.length`, which
     * would be the single-row special case the contract forbids.
     */
    hasRowHeaders?: boolean
    /**
     * Where the board's outline attaches to the page around it. The board owns the shape
     * below: overview at this level, column names one below, swimlane names at this
     * level, cell heading at this level (flat) or one below (swimlaned). Defaults to `2`.
     * Capped at `4`.
     */
    headingLevel?: BoardHeadingLevel
    /**
     * Put both scroll containers back to the start whenever this changes, on both axes
     * and without animating.
     *
     * The board does not restore scroll position and cannot: only the caller knows
     * whether the `data` it just supplied is the same board with another page in it, or a
     * different board entirely. Change this when it is a different board. Leave it
     * undefined and the board never resets.
     */
    resetScrollKey?: string | number
    /**
     * How fast the board scrolls itself while a card is dragged near one of its edges, in
     * pixels per animation frame at full velocity. Defaults to a rate that crosses one of
     * `@github-ui/board`'s columns in a little under six tenths of a second on a 60Hz
     * display.
     *
     * One number for both of the board's axes: going across the board and going down it
     * to a swimlane that is off screen are the same journey, and a reader carrying a card
     * into a corner is making both at once.
     *
     * Per animation frame rather than per second, because that is what the scroll loop
     * multiplies, so a display that paints faster scrolls proportionally faster. Velocity
     * is the fraction of the band at the edge that the pointer has crossed, so this is the
     * rate at the very edge of the scrollable content rather than the usual one.
     */
    autoScrollRate?: number
    /**
     * The same, down the cards inside one cell, and slower by default: a card is a smaller
     * target than a column, and a cell you rank by hand is aimed between two of them.
     *
     * Separate from {@link BoardProviderProps.autoScrollRate} rather than derived from it,
     * so a board that states one is not made to restate the other.
     */
    cellAutoScrollRate?: number
    /**
     * The cards that are selected. Defaults to none.
     *
     * The board holds no selection of its own and derives none, so this is the whole of
     * what is selected: a card is selected because it is in this list and for no other
     * reason. Leave it out and nothing is ever selected, whatever anybody clicks.
     *
     * A card is a cell and an item rather than an item alone, because the same item can
     * sit in two cells on a board whose field takes several values, and a caller acting on
     * a selection has to know which of them a reader meant.
     */
    selectedCards?: readonly BoardSelectedCard[]
    /**
     * Called with what a gesture makes of {@link BoardProviderProps.selectedCards}, for the
     * caller to store and hand back.
     *
     * The board applies nothing itself. Supply no handler and the board has no selection at
     * all, which is the state every board was in before this existed.
     *
     * It is called only when a gesture really did change the selection, so clicking the one
     * card already selected reports nothing.
     */
    onSelectionChange?: (selectedCards: readonly BoardSelectedCard[]) => void
    /**
     * Called when a drop moves a card somewhere it was not, with the cards that moved, the
     * cell they were in and the cell they are going to.
     *
     * **Supplying this is what makes the board a drag surface.** Leave it out and there is
     * nobody to tell that a card was dropped, so the board offers no drag at all: no card
     * can be picked up, no cell is a drop target, and nothing is drawn or reported for
     * either. A board that lifted a card, followed the pointer with a copy of it and then
     * discarded the drop would be making an offer it cannot keep.
     *
     * **The board performs no write and holds no move of its own.** It calls this and
     * carries on drawing the `data` it was given, so the card stays exactly where it was
     * until you hand back data that says otherwise. Nothing is held optimistically and
     * nothing is queued.
     *
     * **Return a promise when the move is applied later.** The card that moved is marked
     * as landed, and gets focus back, once the `data` you hand back shows the move. The
     * board waits for that while the promise is pending and for a second after it
     * resolves. With no promise it waits a second from the drop. A rejected promise ends the
     * wait at once, and the rejection is not caught.
     *
     * A drop outside every cell reports nothing, and so does one on a droppable of the
     * caller's own. So does a drop in the cell the card came from, unless
     * {@link BoardProviderProps.canReorderCards} says that cell is ranked by hand, which is
     * the one case where staying in the same cell changes what people see.
     *
     * Which of the cards on a board that moves cards may be moved is stated per card
     * through {@link BoardProviderProps.getItemDragState}, and where each may land through
     * {@link BoardProviderProps.canDropItem}. This holds no rule of its own on top of them.
     */
    onItemsMove?: (move: BoardItemsMove) => void | Promise<unknown>
    /**
     * Whether the cards in one cell are in an order somebody chose, rather than the order
     * the board's sort puts them in. Defaults to no cell being hand-ordered.
     *
     * **This is what turns a cell into a place a card can be aimed at.** Say `true` for a
     * cell and a drag over it draws where the card would go, a drop reports that place as
     * {@link BoardItemsMove.anchor}, and dropping a card among its own neighbours becomes a
     * move rather than nothing. Say nothing at all and the board behaves exactly as it did
     * before this existed: the cells are the only drop targets, a drop says which cell and
     * no more, and a drop in the cell the card came from reports nothing.
     *
     * Only the caller knows. A cell ordered by a sort has no position for a reader to aim
     * at, because the sort would put the card back wherever it was dropped; a cell ordered
     * by hand is one where that gesture is the whole point. The board holds no rule about
     * which it is and derives none from the data.
     *
     * Asked per cell rather than per board, because one board can hold both: a column
     * standing for a value nobody ranks sits beside one that is ranked.
     *
     * Whether the board moves any card at all is the separate question
     * {@link BoardProviderProps.onItemsMove} answers, and it is answered first: leave that
     * out and no cell is a drop target, whatever this says.
     */
    canReorderCards?: (cell: CellAddress) => boolean
    /**
     * Whether one card may land in one cell. Defaults to every drop being allowed.
     *
     * Asked while a card is held, for the cell under the pointer. A refused cell is never
     * the one a drop lands in: `isOver` stays `false`, no place inside it is drawn, and a
     * drop there reports nothing to {@link BoardProviderProps.onItemsMove}.
     *
     * Never asked about the cell the card came from, so reordering a card in place and
     * dropping it back where it was work whatever this returns.
     *
     * {@link BoardProviderProps.getItemDragState} says whether a card can be picked up at
     * all. This says where it can go once it is, so a card that may change its column and
     * not its row is draggable there and refused here for every cell in another row.
     *
     * Called with the caller's own item, resolved from the held card's address against the
     * `data` above. A card the caller's data no longer holds may land anywhere rather than
     * being refused on its behalf.
     */
    canDropItem?: (item: TItem, move: {from: CellAddress; to: CellAddress}) => boolean
    children: ReactNode
  }

const NO_SELECTED_CARDS: readonly BoardSelectedCard[] = []

const DRAGGABLE: BoardItemDragState = {draggable: true}

/**
 * What a board that says nothing about dragging answers, which is the board every caller
 * had before this existed. Declared here rather than inline so the config keeps one
 * identity across renders for a caller that supplies nothing.
 */
function everyCardIsDraggable(): BoardItemDragState {
  return DRAGGABLE
}

/**
 * What a board that says nothing about hand-ordering answers, which is the board every
 * caller had before this existed. Declared here rather than inline so that a caller
 * supplying nothing hands the context one identity for the life of the board.
 */
function noCellIsHandOrdered(): boolean {
  return false
}

/**
 * What a board that says nothing about drops answers, which is the board every caller had
 * before this existed. Declared here for the same reason as the two defaults above.
 */
function everyDropIsAllowed(): boolean {
  return true
}

/**
 * Publishes the board's contexts. Renders no DOM of its own, so a caller is free to
 * put the provider anywhere in its tree and lay the board out however it likes.
 */
export function BoardProvider<TItem>({
  data,
  getItemId,
  getItemLabel,
  getItemDragState = everyCardIsDraggable,
  labels,
  hasRowHeaders = false,
  headingLevel = DEFAULT_BOARD_HEADING_LEVEL,
  resetScrollKey,
  autoScrollRate = BOARD_AUTO_SCROLL_RATE,
  cellAutoScrollRate = CELL_AUTO_SCROLL_RATE,
  selectedCards = NO_SELECTED_CARDS,
  onSelectionChange,
  onItemsMove,
  canReorderCards = noCellIsHandOrdered,
  canDropItem = everyDropIsAllowed,
  onLoadMoreItems,
  onApproachItems,
  onLoadMoreColumns,
  onLoadMoreRows,
  onVisibleKeysChange,
  onFocusedCardChange,
  onCardDialogChange,
  onDragChange,
  children,
}: BoardProviderProps<TItem>): ReactNode {
  // Written by `useBoardRoot` when the caller's own element mounts, and read by the
  // paging sentinels inside an event handler. A ref rather than state, so a board that
  // mounts a root does not re-render every card it has just drawn.
  const boardElementRef = useRef<HTMLElement | null>(null)
  const [collapsedRows, setCollapsedRows] = useState<ReadonlySet<RowKey>>(() => new Set())
  const setRowCollapsed = useCallback((rowKey: RowKey, collapsed: boolean) => {
    setCollapsedRows(current => {
      if (current.has(rowKey) === collapsed) return current

      const next = new Set(current)
      if (collapsed) next.add(rowKey)
      else next.delete(rowKey)
      return next
    })
  }, [])
  const dataValue = useMemo<BoardDataContextValue>(() => {
    const columnsByKey = new Map(data.columns.map(column => [column.key, column]))
    const rowsByKey = new Map(data.rows.map(row => [row.key, row]))

    const cellsByAddress = new Map<string, BoardCell<unknown>>()
    for (const cell of data.cells) {
      cellsByAddress.set(composeCellKey(cell), cell)
    }

    return {
      data,
      columnsByKey,
      rowsByKey,
      cellsByAddress,
      itemIndex: createCellItemIndex(cellsByAddress),
    }
  }, [data])

  const config = useMemo<BoardConfig>(
    // The accessors are contravariant in `TItem`, so the context holds them widened and
    // the typed hooks narrow them back. The provider is the only place that knows both
    // sides, which is what makes the cast safe.
    () =>
      ({
        getItemId,
        getItemLabel,
        labels,
        hasRowHeaders,
        resetScrollKey,
        autoScrollRate,
        cellAutoScrollRate,
      }) as BoardConfig,
    [getItemId, getItemLabel, labels, hasRowHeaders, resetScrollKey, autoScrollRate, cellAutoScrollRate],
  )

  /*
    The caller's answer for one card, resolved from the address the card is drawn at.

    Built here rather than in the card so that `useBoardCard`, the one hook that runs per
    drawn card, subscribes to the three things that can change the answer and to nothing
    else. Reading the config there instead would wake every card whenever a label,
    `getItemLabel`, `hasRowHeaders` or `resetScrollKey` changed identity, none of which a
    card reads.

    The item is resolved through the index rather than passed, because a card is addressed
    by its cell and its id while the caller's predicate takes the caller's own item. The
    selected-card resolver reads the same index: the first card in a cell builds it and
    every card after that is a `Map` lookup, so a drawn cell is scanned once rather than
    once per card in it.
  */
  const {itemIndex} = dataValue
  const dragPolicy = useMemo<BoardCardDragPolicy>(
    () => (cellKey, itemId) => {
      // A card with no id cannot be looked up: an empty id names every such card in the
      // cell, so an answer would as likely be a different card's. It cannot be dragged
      // either way, and no reason for that is the caller's to have given.
      if (itemId === '') return undefined

      const item = itemIndex.itemIn(cellKey, itemId, getItemId as (item: unknown) => ItemId)
      // A card the caller's data no longer holds is left draggable. The caller was never
      // asked about it, so refusing it would be the board deciding.
      if (item === undefined) return undefined

      const dragState = getItemDragState(item as TItem)

      return dragState.draggable ? undefined : dragState.reason
    },
    [itemIndex, getItemId, getItemDragState],
  )

  // The caller's answer for one drop, resolved through the same index. The cell a card came
  // from is allowed without asking, so a refused axis never stops a reorder in place.
  const dropPolicy = useMemo<BoardItemDropPolicy>(
    () => (card, to) => {
      if (card.columnKey === to.columnKey && card.rowKey === to.rowKey) return true

      const item = itemIndex.itemIn(composeCellKey(card), card.itemId, getItemId as (item: unknown) => ItemId)
      // Left droppable for the reason a missing card is left draggable above.
      if (item === undefined) return true

      return canDropItem(item as TItem, {from: {columnKey: card.columnKey, rowKey: card.rowKey}, to})
    },
    [itemIndex, getItemId, canDropItem],
  )

  // Its own context rather than part of the config, because a caller building these
  // inline hands over new functions on every render, and a card that only reads a
  // label must not re-render for that.
  const paging = useMemo<BoardPaging>(
    () => ({onLoadMoreItems, onApproachItems, onLoadMoreColumns, onLoadMoreRows, onVisibleKeysChange}),
    [onLoadMoreItems, onApproachItems, onLoadMoreColumns, onLoadMoreRows, onVisibleKeysChange],
  )

  // Casts widen each arithmetic result from `number` to `BoardHeadingElementLevel`;
  // the input's own range guarantees safety, but TypeScript cannot narrow union+literal sums.
  const headingLevels = useMemo<BoardHeadingLevels>(
    () => ({
      overview: headingLevel,
      column: (headingLevel + 1) as BoardHeadingElementLevel,
      row: headingLevel,
      cell: (hasRowHeaders ? headingLevel + 1 : headingLevel) as BoardHeadingElementLevel,
    }),
    [headingLevel, hasRowHeaders],
  )

  return (
    <BoardHeadingLevelsContext value={headingLevels}>
      <BoardConfigContext value={config}>
        <BoardPagingContext value={paging}>
          {/*
          Innermost, because the board the focus reducer moves over is built from the
          data and the config above it. It renders no DOM either.
        */}
          <BoardDataContext value={dataValue}>
            <BoardCollapsedRowsContext value={collapsedRows}>
              <BoardRowCollapseContext value={setRowCollapsed}>
                {/*
                Below the data and config it is built from, and above everything a card is
                drawn in. It carries one answer per card and nothing else, so a card reads it
                without reading either of them.
              */}
                <BoardCardDragPolicyContext value={dragPolicy}>
                  {/*
                Inside the data context for the same reason the focus provider is: a range
                runs over the cards one cell holds, which is a fact stated above it. It
                renders no DOM either.
              */}
                  {/*
                Beside the drag policy rather than in the config, for the same reason: only a
                cell asks it, and the config around it is rebuilt whenever a label or an
                accessor changes identity.
              */}
                  <BoardCellOrderingContext value={canReorderCards}>
                    <BoardSelectionProvider selectedCards={selectedCards} onSelectionChange={onSelectionChange}>
                      <BoardFocusProvider>
                        {/*
                  Above the reporting provider, because the drag report is one of the things
                  the provider renders itself, and below the data context, because resolving
                  where a card was dropped reads the items a cell has loaded.
                */}
                        <BoardItemDropPolicyContext value={dropPolicy}>
                          {/*
                          Above the drag provider, which reads the board's element to tell focus a
                          reader moved from focus that fell when a moved card left the page.
                        */}
                          <BoardElementRefContext value={boardElementRef}>
                            <BoardDragProvider onItemsMove={onItemsMove}>
                              {/*
                    Innermost of all, because what it reports about focus is read from the
                    focus store above it and what it reports about a drag from the drag
                    context. Nothing it mounts draws anything: reporting where focus is must
                    not put a value on screen that every card would have to re-render for.
                  */}
                              <BoardReportingProvider
                                onFocusedCardChange={onFocusedCardChange}
                                onCardDialogChange={onCardDialogChange}
                                onDragChange={onDragChange}
                              >
                                {children}
                              </BoardReportingProvider>
                            </BoardDragProvider>
                          </BoardElementRefContext>
                        </BoardItemDropPolicyContext>
                      </BoardFocusProvider>
                    </BoardSelectionProvider>
                  </BoardCellOrderingContext>
                </BoardCardDragPolicyContext>
              </BoardRowCollapseContext>
            </BoardCollapsedRowsContext>
          </BoardDataContext>
        </BoardPagingContext>
      </BoardConfigContext>
    </BoardHeadingLevelsContext>
  )
}
